/**
 * Registers concrete handlers for the offline sync queue.
 * Without these, queued writes sit in IndexedDB forever — processSyncQueue
 * skips any type that has no handler.
 */

import { registerSyncHandler } from './sync';
import { submitGuestSession } from '@/services/guest.service';
import { getCurrentUser } from '@/services/auth.service';

type PhotoSessionPayload = {
  email: string;
  orgId: string;
  questionnaireId: string;
  responses: Record<string, string | number | boolean | string[]>;
  photoDataUrls: string[];
  sessionId?: string;
};

export function registerAppSyncHandlers(): void {
  registerSyncHandler('photoSession', async (write) => {
    const payload = write.payload as PhotoSessionPayload;

    // A malformed payload (e.g. a stale entry from an older app version with
    // a different shape) can never succeed — drop it instead of letting it
    // poison the queue with retries forever.
    if (
      typeof payload?.email !== 'string' ||
      typeof payload?.orgId !== 'string' ||
      !Array.isArray(payload?.photoDataUrls)
    ) {
      console.error('Dropping malformed queued photoSession write:', write.id);
      return;
    }

    // Replaying under a different (or no) signed-in account would attach the
    // guest to the wrong organization and fail the storage upload — keep the
    // write queued until the org that captured it is signed in again.
    const user = getCurrentUser();
    if (!user || user.uid !== payload.orgId) {
      throw new Error(
        'Queued photo session belongs to a different signed-in organization'
      );
    }

    await submitGuestSession(
      payload.email,
      payload.orgId,
      payload.questionnaireId,
      payload.responses,
      payload.photoDataUrls,
      payload.sessionId
    );
  });
}
